import {
  EnergyLabelMetadata,
  energyLabelResponseSchema,
  EnergyLabelResults,
} from "../data/energyLabelResponseSchema";

export const timeout = 500;
const yourekoHost = "https://api.youreko.com";
const apiVersion = "v1";
const region = "en-gb";
const partnerName = "john-lewis";

export type EnergyLabelsResponse = {
  responseMetaData?: EnergyLabelMetadata;
  results?: EnergyLabelResults;
  error?: string;
};

type YourekoOptions = {
  showTemplates: boolean;
  productTypes: Array<string | undefined>;
  productTitles: string;
};

const fetchEnergyLabels = async (
  sellingProductType: string,
  title: string,
): Promise<EnergyLabelsResponse> => {
  const path = `${yourekoHost}/${apiVersion}/${region}/${partnerName}/energy-labels.json`;

  const params: YourekoOptions = {
    showTemplates: false,
    productTypes: [getProductType(sellingProductType)],
    productTitles: title,
  };

  const url = `${path}?${optionsToQueryString(params)}`;
  const { controller, timeoutId } = setControllerTimeout();

  return fetch(url, {
    signal: controller.signal,
  })
    .then(async (res) => {
      if (res.ok) {
        const result: unknown = await res.json();
        const parsed = energyLabelResponseSchema.safeParse(result);

        if (parsed.success) {
          const thisProduct = parsed.data.results[title];

          if (!thisProduct || !thisProduct.productFound) {
            throw new Error("Product not found");
          }

          return {
            responseMetaData: parsed.data.responseMetaData,
            results: parsed.data.results,
          };
        }

        throw new Error("Energy Label response parse failed");
      }

      throw new Error("Energy Label request failed");
    })
    .catch((e) => {
      if (e.name === "AbortError") {
        return { error: "Energy Label request timed out" };
      }

      return { error: e.message || "Energy Label request failed" };
    })
    .finally(() => {
      clearTimeout(timeoutId);
    });
};

const setControllerTimeout = () => {
  const controller = new AbortController();

  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeout);

  return { controller, timeoutId };
};

const optionsToQueryString = (options: YourekoOptions) => {
  const { showTemplates } = options;
  const productTypes = encodeURIComponent(options.productTypes.join(","));
  const productTitles = encodeURIComponent(options.productTitles);
  return `showTemplates=${showTemplates}&productTypes[]=${productTypes}&productTitles=${productTitles}`;
};

const getProductType = (sellingProductType: string | undefined) => {
  if (!sellingProductType) return;

  return {
    "Fridge Freezers": "fridge-freezers",
    Freezers: "freezers",
    Fridges: "fridges",
    "Washing Machines": "washing-machines",
    "Washer Dryers": "washer-dryers",
    "Tumble Dryers": "tumble-dryers",
    Dishwashers: "dishwashers",
  }[sellingProductType];
};

export default fetchEnergyLabels;
